import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ICancelOrderPayload, IImportOrderPayload, IOrderEntity, IOrdersListPayload, IPickingFlowResponse, OrderStatusEnum } from "../../../Services/api/orders/orders.interface";
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase";
import { ListProductUseCase } from "../../../Usecase/Products/list-product-usecase";
import { ReprocessOrderPaymentsUseCase } from "../../../Usecase/OrderPayments/reprocess-order-payments-usecase";
import IKanbanBoardOrdersSchemaProps from "../../../Components/KanbanBoardOrders/interfaces/kanban-board-orders-schema.interface";
import { Task } from "../../../Components/KanbanBoardOrders/kanban-board-orders.component";
import { IOrderData } from "../../../Components/Modals/variants/order-details/order-details-modal.component";
import { PickingFlowUseCase } from "../../../Usecase/Orders/picking-flow.usecase";
import { UpdateOrdersUseCase } from "../../../Usecase/Orders/update-order-usecase";
import { ExportInPickingOrderUseCase } from "../../../Usecase/Orders/export-in-picking-order.usecase";
import { ISaleEntity } from "../../Financial/Details/financial-details.hook";
import { ShowOrdersUseCase } from "../../../Usecase/Orders/show-order-usecase";
import { useAuth } from "../../../Hooks/Auth";
import { CancelOrderUseCase } from "../../../Usecase/Orders/cancel-order-usecase";
import { ImportOrderUseCase } from "../../../Usecase/Orders/import-order-usecase";
import { TStoreType } from "../../../Services/api/service-orders/service-order.interface";
import { Environment } from "../../../Config/constants";
import { GridSortModel } from "@mui/x-data-grid";
import { mapApiOrderFieldToGrid, mapGridOrderFieldToApi, isTotalValueSortField, sortOrdersByTotalValue } from "../../../utils/order-list-sort";
import { normalizeOrderEntity } from "../../../utils/normalize-order-entity";
import { buildSaleFromOrder } from "../../../utils/build-sale-from-order";


interface IUseListOrdersProps {
    listProductUseCase: ListProductUseCase
    listOrdersUseCase: ListOrdersUseCase
    reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase
    pickingFlowUseCase: PickingFlowUseCase
    UpdateOrdersUseCase: UpdateOrdersUseCase
    exportInPickingOrderUseCase: ExportInPickingOrderUseCase
    showOrdersUseCase: ShowOrdersUseCase
    cancelOrderUseCase: CancelOrderUseCase
    importOrderUseCase: ImportOrderUseCase
}

export function useListOrders({ pickingFlowUseCase, listOrdersUseCase, reprocessOrderPaymentsUseCase, UpdateOrdersUseCase, exportInPickingOrderUseCase, showOrdersUseCase, cancelOrderUseCase, importOrderUseCase }: IUseListOrdersProps){
    const [listLoading, setListLoading] = useState(true)
    const [cancellingLoading, setCancellingLoading] = useState(true)
    const [pickingLoading, setPickingLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [loadErrorCancelling, setLoadErrorCancelling] = useState(false)
    const [page, setPage] = useState(1)
    const [pageCancelling, setPageCancelling] = useState(1)
    const [data, setData] = useState<IOrderEntity[]>([])
    const [dataCancelling, setDataCancelling] = useState<IOrderEntity[]>([])
    const [showDetails, setShowDetails] = useState<undefined | string>(undefined)
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [totalRegistersCancelling, setTotalRegistersCancelling] = useState(0)
    const [showModalChoices, setShowModalChoices] = useState(false)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const [currentFilters, setCurrentFilters] = useState<IOrdersListPayload>({ page: 1, store: undefined })
    const [currentFiltersCancelling, setCurrentFiltersCancelling] = useState<IOrdersListPayload>({ page: 1 })
    const [currentFiltersPickingFlow, setCurrentFiltersPickingFlow] = useState<Omit<IOrdersListPayload, 'page'>>({ store: 'B2B' })
    const [orderToReprocess, setOrderToReprocess] = useState<string | undefined>(undefined)
    const [kanbanSchema, setKanbanSchema] = useState<IKanbanBoardOrdersSchemaProps>()
    const [orderToCancel, setOrderToCancel] = useState<IOrderEntity | undefined>(undefined)
    const [showImportOrderModal, setShowImportOrderModal] = useState(false)
    const { user } = useAuth()
    const [value, setValue] = useState(0);
    const [orderDetailsData, setOrderDetailsData] = useState<{
        sale?: ISaleEntity;
        orderData?: IOrderData;
    } | undefined>(undefined);
    const [clientSortedPool, setClientSortedPool] = useState<IOrderEntity[] | null>(null)

    const pageSize = Number(Environment.OFFSET_REGISTERS) || 50
    const listSearchGenerationRef = useRef(0)

    const isStaleListSearch = (searchGeneration?: number) =>
      searchGeneration !== undefined && searchGeneration !== listSearchGenerationRef.current

    const bumpListSearchGeneration = () => {
      listSearchGenerationRef.current += 1
      return listSearchGenerationRef.current
    }

    const { showToast } =  useToast()

    const hasCancelPermission = useMemo(() => {
      return user?.permissionsScreens?.includes(`feature:orders-cancel:manage`)
    }, [user])
   
    const transformData = (dados: IPickingFlowResponse[]) => {
      const tasks: Record<string, Task> = {};
      const waitingPicking: string[] = [];
      const inPicking: string[] = [];
      const awaitInvoicing: string[] = [];
      
      dados.forEach((item) => {
          const taskId = item.orderId;
          tasks[taskId] = {
              id: taskId,
              content: {
                  order: `Pedido #${item.nsOrderId}`,
                  nsOrderId: item.nsOrderId,
                  customer: item.customerName,
                  customerName: item.customerName,
                  carrier: item.carrierName,
                  carrierName: item.carrierName,
                  type: 'click',
                  observations: item.orderObservations || undefined,
              }
          };

          if (item.orderStatus === OrderStatusEnum.AWAITING_PICKING) {
            waitingPicking.push(taskId);
          } else if (item.orderStatus === OrderStatusEnum.IN_PICKING) {
              inPicking.push(taskId);
          } else if (item.orderStatus === OrderStatusEnum.AWAITING_INVOICING) { 
              awaitInvoicing.push(taskId);
          }
      });

      const result = {
          tasks,
          columns: {
            [OrderStatusEnum.AWAITING_PICKING]: {
                  id: OrderStatusEnum.AWAITING_PICKING,
                  title: 'Aguardando Separação',
                  taskIds: waitingPicking,
              },
              [OrderStatusEnum.IN_PICKING]: {
                  id: OrderStatusEnum.IN_PICKING,
                  title: 'Em Separação',
                  taskIds: inPicking,
              },
              [OrderStatusEnum.AWAITING_INVOICING]: {
                  id: OrderStatusEnum.AWAITING_INVOICING,
                  title: 'Aguardando Faturamento',
                  taskIds: awaitInvoicing,
              },
          },
          columnOrder: [OrderStatusEnum.AWAITING_PICKING, OrderStatusEnum.IN_PICKING, OrderStatusEnum.AWAITING_INVOICING],
      };

      return result;
    }

      const handleUpdateOrderStatus = async (payload: { orderId: string, newStatus: OrderStatusEnum }) => {
        try {
          setActionLoading(true)
          
          const listResult = await UpdateOrdersUseCase.handle({ orderId: payload.orderId, newStatus: payload.newStatus });

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            throw new Error(String(listResult?.error?.error));
          }

          showToast('Status do pedido atualizado com sucesso!', 'success');
        } catch (error) {
          console.error("error handleUpdateOrderStatus:", error)
          if (!(error instanceof Error)) {
            showToast('Não foi possível atualizar o status do pedido.', 'error')
          }
          throw error;
        } finally {
          setActionLoading(false);
        }
    };

    const handlePrintPickingList = async () => {
      try {
        setPickingLoading(true)
        const response = await exportInPickingOrderUseCase.handle();

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }
        
        showToast('Lista de separação baixada com sucesso!', 'success');
      } catch (error) {
        console.error("error handlePrintPickingList:", error);
        showToast('Erro ao gerar lista de separação.', 'error');
      } finally {
        setPickingLoading(false);
      }
    };

    const handleCardClick = async (orderId: string) => {
      
      try {
        setActionLoading(true);
        
        const orderResult = await showOrdersUseCase.handle(orderId);

        if (orderResult.isFailure) {
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }

        const result = orderResult.getValue();

        if (result?.id) {
          const saleData = buildSaleFromOrder(result);

          const orderData: IOrderData = {
            id: result.id,
            nsOrderId: result.nsOrderId || '',
            createdAt: result.createdAt,
            customer: result.customer ? {
              id: result.customer.id,
              name: result.customer.name,
              email: result.customer.email,
              phone: result.customer.phone || ''
            } : undefined,
            seller: result.seller ? {
              id: result.seller.id,
              name: result.seller.name || ''
            } : undefined,
            sellerAlreadyAdded: !!result.seller,
            observations: result.observations
          };

          setOrderDetailsData({
            sale: saleData,
            orderData: orderData
          });
        }
      } catch (error) {
        console.error("error handleCardClick:", error);
        showToast('Erro ao carregar detalhes do pedido.', 'error');
      } finally {
        setActionLoading(false);
      }
    };

    const listPickingFlow = async (payload: Partial<IOrdersListPayload> = {}, searchGeneration?: number) => {
        try {
          setPickingLoading(true)
          
          const { searchBy, status, productionStatus, store, startDate, endDate } = payload;
          
          // Merge current filters with new search/filter parameters
          // Use the provided values if they exist (including empty arrays), otherwise keep current filters
          const filters: Omit<IOrdersListPayload, 'page'> = {
            ...currentFiltersPickingFlow,
            store: 'B2B',
          }

          // Apply status if provided (including empty array to clear filters)
          if (status !== undefined) {
            filters.status = status;
          }

          if (productionStatus !== undefined) {
            filters.productionStatus = productionStatus;
          }

          // Apply store from payload only for metadata; fluxo B2B é fixo na separação
          if ('store' in payload) {
            if (store === null || store === undefined) {
              delete filters.store;
            }
          }

          filters.store = 'B2B';

          // Apply dates: if explicitly passed (even as undefined), apply or remove them
          if ('startDate' in payload) {
            if (startDate === null || startDate === undefined) {
              delete filters.startDate;
            } else {
              filters.startDate = startDate;
            }
          }

          if ('endDate' in payload) {
            if (endDate === null || endDate === undefined) {
              delete filters.endDate;
            } else {
              filters.endDate = endDate;
            }
          }

          // Add searchBy only if it has a value
          if (searchBy && searchBy.trim() !== '') {
            filters.searchBy = searchBy;
          } else if (searchBy === '' || searchBy === null || searchBy === undefined) {
            delete filters.searchBy;
          }
          
          // Update current filters
          setCurrentFiltersPickingFlow(filters)
          
          const listResult = await pickingFlowUseCase.handle(filters);

          if (isStaleListSearch(searchGeneration)) return

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

          const result = listResult.getValue()

          const normalizeSchema : any = transformData(result ?? [])
    
          setKanbanSchema(normalizeSchema)
        } catch (error) {
          if (!isStaleListSearch(searchGeneration)) {
            console.error("error listPickingFlow:", error)
            showToast('Erro ao carregar a separação B2B.', 'error')
          }
        } finally {
          if (searchGeneration === undefined || !isStaleListSearch(searchGeneration)) {
            setPickingLoading(false);
          }
        }
    };

    const listOrders = async ({ page: currentPage, searchBy, status, productionStatus, store, startDate, endDate, orderBy }: IOrdersListPayload, searchGeneration?: number) => {
        try {
          setListLoading(true)
          setLoadError(false)
          setClientSortedPool(null)
          
          // Merge current filters with new search/filter parameters
          const filters: IOrdersListPayload = {
            ...currentFilters,
            page: currentPage || currentFilters.page,
            status: status !== undefined ? status : currentFilters.status,
            productionStatus: productionStatus !== undefined ? productionStatus : currentFilters.productionStatus,
            store,
            startDate: startDate !== undefined ? startDate : currentFilters.startDate,
            endDate: endDate !== undefined ? endDate : currentFilters.endDate,
            orderBy: orderBy !== undefined ? orderBy : currentFilters.orderBy,
          }

          // Add searchBy only if it has a value
          if (searchBy && searchBy.trim() !== '') {
            filters.searchBy = searchBy;
          } else {
            delete filters.searchBy;
          }
          
          // Update current filters
          setCurrentFilters(filters)
          
          const listResult = await listOrdersUseCase.handle(filters);

          if (isStaleListSearch(searchGeneration)) return

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

          const list = listResult.getValue()
         
          if(list?.data){
            setData(list.data.map(normalizeOrderEntity))
            setTotalRegisters(list.total)
            setLoadError(false)
          }

          setPage(filters.page)
        } catch (error) {
          if (!isStaleListSearch(searchGeneration)) {
            setLoadError(true)
            console.error("error listOrders:", error)
            showToast('Erro ao carregar os pedidos.', 'error')
          }
        } finally {
          if (searchGeneration === undefined || !isStaleListSearch(searchGeneration)) {
            setListLoading(false);
          }
        }
    };

    const fetchAllOrdersMatchingFilters = async (baseFilters: IOrdersListPayload): Promise<IOrderEntity[]> => {
      const { orderBy: _orderBy, page: _page, offset: _offset, ...sanitized } = baseFilters

      const firstResult = await listOrdersUseCase.handle({ ...sanitized, page: 1 })
      if (firstResult.isFailure) {
        showToast(String(firstResult?.error?.error), 'error')
        return []
      }

      const firstValue = firstResult.getValue()
      const total = firstValue?.total ?? 0
      const firstBatch = firstValue?.data ?? []

      if (firstBatch.length >= total || total === 0) {
        return firstBatch
      }

      const bulkResult = await listOrdersUseCase.handle({
        ...sanitized,
        page: 1,
        offset: total,
      })

      if (!bulkResult.isFailure) {
        const bulkData = bulkResult.getValue()?.data ?? []
        if (bulkData.length >= total || bulkData.length > firstBatch.length) {
          return bulkData
        }
      }

      const allOrders = [...firstBatch]
      const totalPages = Math.ceil(total / pageSize)

      for (let current = 2; current <= totalPages; current += 1) {
        const pageResult = await listOrdersUseCase.handle({ ...sanitized, page: current })
        if (pageResult.isFailure) break

        const pageData = pageResult.getValue()?.data ?? []
        allOrders.push(...pageData)
        if (pageData.length < pageSize) break
      }

      return allOrders
    }

    const sortOrdersByTotal = async (order: 'ASC' | 'DESC', filters: IOrdersListPayload) => {
      try {
        setListLoading(true)
        const allOrders = await fetchAllOrdersMatchingFilters(filters)
        const sorted = sortOrdersByTotalValue(allOrders.map(normalizeOrderEntity), order)

        setClientSortedPool(sorted)
        setTotalRegisters(sorted.length)
        setPage(1)
        setCurrentFilters({
          ...filters,
          page: 1,
          orderBy: { field: 'total_value', order },
        })
        setData(sorted.slice(0, pageSize))
      } catch (error) {
        setLoadError(true)
        console.error('error sortOrdersByTotal:', error)
        showToast('Erro ao ordenar pedidos por total.', 'error')
      } finally {
        setListLoading(false)
      }
    }

    const listOrdersCancelling = async ({ page: currentPage, searchBy, orderBy, ...rest }: IOrdersListPayload, searchGeneration?: number) => {
      try {
        setCancellingLoading(true)
        setLoadErrorCancelling(false)
        
        const filters: IOrdersListPayload = {
          ...currentFiltersCancelling,
          ...rest,
          page: currentPage || currentFiltersCancelling.page,
          orderBy: orderBy !== undefined ? orderBy : currentFiltersCancelling.orderBy,
          status: [OrderStatusEnum.OPEN, OrderStatusEnum.PROCESSING, OrderStatusEnum.AWAITING_PICKING, OrderStatusEnum.IN_PICKING, OrderStatusEnum.AWAITING_INVOICING, OrderStatusEnum.INVOICING, OrderStatusEnum.INVOICE_PROBLEM, OrderStatusEnum.PARTIALLY_INVOICED],
        }

        // Add searchBy only if it has a value
        if (searchBy && searchBy.trim() !== '') {
          filters.searchBy = searchBy;
        } else {
          delete filters.searchBy;
        }
        
        // Update current filters
        setCurrentFiltersCancelling(filters)
        
        const listResult = await listOrdersUseCase.handle(filters);

        if (isStaleListSearch(searchGeneration)) return

        if (listResult.isFailure) {
          setLoadErrorCancelling(true)
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        const list = listResult.getValue()
       
        if(list?.data){
          setDataCancelling(list.data.map(normalizeOrderEntity))
          setTotalRegistersCancelling(list.total)
          setLoadErrorCancelling(false)
        }

        setPageCancelling(filters.page)
      } catch (error) {
        if (!isStaleListSearch(searchGeneration)) {
          setLoadErrorCancelling(true)
          console.error("error listOrdersCancelling:", error)
          showToast('Erro ao carregar os cancelamentos.', 'error')
        }
      } finally {
        if (searchGeneration === undefined || !isStaleListSearch(searchGeneration)) {
          setCancellingLoading(false);
        }
      }
  };

  const handlePickingSearch = (payload: { searchBy?: string }) => {
    const searchBy = payload.searchBy?.trim() || undefined;
    const searchGeneration = bumpListSearchGeneration()

    setCurrentFilters((prev) => {
      const next = { ...prev, page: 1 };
      if (searchBy) {
        next.searchBy = searchBy;
      } else {
        delete next.searchBy;
      }
      return next;
    });

    listPickingFlow({
      ...currentFiltersPickingFlow,
      searchBy,
      store: 'B2B',
      startDate: currentFilters.startDate,
      endDate: currentFilters.endDate,
    }, searchGeneration);
  };

  const refreshPickingFlow = () => {
    listPickingFlow({
      ...currentFiltersPickingFlow,
      searchBy: currentFilters.searchBy,
      startDate: currentFilters.startDate,
      endDate: currentFilters.endDate,
      store: 'B2B',
    });
  };

  const clearPickingSearch = () => {
    handlePickingSearch({ searchBy: '' });
  };

  const handleSearch = (payload: IOrdersListPayload) => {
    const searchGeneration = bumpListSearchGeneration()
    setClientSortedPool(null)
    listOrders({ ...currentFilters, ...payload, page: 1 }, searchGeneration)
    listOrdersCancelling({ ...currentFiltersCancelling, ...payload, page: 1 }, searchGeneration)
    listPickingFlow({ 
      store: 'B2B',
      startDate: payload.startDate ?? currentFilters.startDate,
      endDate: payload.endDate ?? currentFilters.endDate,
      status: payload.status,
      productionStatus: payload.productionStatus,
      searchBy: payload.searchBy,
    }, searchGeneration)
  }

    const handleImportOrder = async ({ orderId, store }: IImportOrderPayload) => {
      try {
        setActionLoading(true)

  
        const response = await importOrderUseCase.handle({ orderId, store });

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }

        showToast('Pedido importado com sucesso', 'success');
        setShowImportOrderModal(false)
      } catch (error) {
        console.error("error handleImportOrder:", error)
        showToast('Erro ao importar o pedido.', 'error')
      } finally {
        setActionLoading(false);
      }
  };



  

    const list = useMemo(() => {
      if (clientSortedPool) {
        const start = (page - 1) * pageSize
        return clientSortedPool.slice(start, start + pageSize)
      }

      return data ?? []
    }, [clientSortedPool, data, page, pageSize])

    const listCancelling = useMemo(() => dataCancelling ?? [], [dataCancelling])

    const ordersSortModel = useMemo<GridSortModel>(() => {
      const orderBy = currentFilters.orderBy
      if (!orderBy?.field || !orderBy?.order) return []

      return [{
        field: mapApiOrderFieldToGrid(orderBy.field),
        sort: orderBy.order.toLowerCase() as 'asc' | 'desc',
      }]
    }, [currentFilters.orderBy])

    const cancellingSortModel = useMemo<GridSortModel>(() => {
      const orderBy = currentFiltersCancelling.orderBy
      if (!orderBy?.field || !orderBy?.order) return []

      return [{
        field: mapApiOrderFieldToGrid(orderBy.field),
        sort: orderBy.order.toLowerCase() as 'asc' | 'desc',
      }]
    }, [currentFiltersCancelling.orderBy])

    const handleShowDetails = (code: string) => {
      setShowDetails(code)
    }

    const handleFilterSelect = (payload: IOrdersListPayload) => {
      const searchGeneration = bumpListSearchGeneration()
      setClientSortedPool(null)
      // Extract page from payload for listOrders, but not for listPickingFlow
      const { page, ...pickingFlowPayload } = payload;
      listOrders({ ...currentFilters, ...payload, page: 1 }, searchGeneration)
      listOrdersCancelling({ ...currentFiltersCancelling, ...payload, page: 1 }, searchGeneration)
      // Apply filters directly to picking flow, explicitly passing store and dates
      // Using object with explicit properties ensures they are processed even if undefined
      listPickingFlow({ 
        store: 'B2B',
        startDate: payload.startDate ?? currentFilters.startDate,
        endDate: payload.endDate ?? currentFilters.endDate,
        status: payload.status,
        productionStatus: payload.productionStatus,
        searchBy: payload.searchBy ?? currentFilters.searchBy,
      }, searchGeneration)
    }

    const handleReprocessPayment = async (orderId: string) => {
        try {
          setActionLoading(true)
          
        const response =  await reprocessOrderPaymentsUseCase.handle(orderId);

          if (response.isFailure) {
            showToast(String(response?.error?.error), 'error');
            return;
          }

          setOrderToReprocess(undefined)
          showToast('Pagamento reprocessado com sucesso', 'success');
          return;
        } catch (error) {
          console.error("error handleReprocessPayment:", error)
          showToast('Erro ao reprocessar o pagamento.', 'error')
        } finally {
          setActionLoading(false);
        }
    };

    const handleCancelOrder = async (reason?: string) => {
      if(!orderToCancel) return
      try {
        setActionLoading(true)

        const payload : ICancelOrderPayload = {
          orderId: orderToCancel?.id,
          reason: reason
        }
      
        
      const response =  await cancelOrderUseCase.handle(payload);

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }

        setOrderToCancel(undefined)
        showToast('Pagamento cancelado com sucesso', 'success');
        return;
      } catch (error) {
        console.error("error handleCancelOrder:", error)
        showToast('Erro ao cancelar o pedido.', 'error')
      } finally {
        setActionLoading(false);
      }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);

      if (newValue === 1) {
        listPickingFlow({
          ...currentFiltersPickingFlow,
          searchBy: currentFilters.searchBy,
          startDate: currentFilters.startDate,
          endDate: currentFilters.endDate,
          store: 'B2B',
        });
      }
    };

    function a11yProps(index: number) {
      return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
      };
    }

    // Funções wrapper para manter os filtros na paginação
      const handlePageChangeOrders = (data: IOrdersListPayload) => {
        window.scrollTo({ top: 0, behavior: 'smooth' })

        if (clientSortedPool) {
          setPage(data.page || 1)
          return
        }

        listOrders({ ...currentFilters, ...data })
    }

    const handlePageChangeCancelling = (data: IOrdersListPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
        listOrdersCancelling({ ...currentFiltersCancelling, ...data })
    }

    const handleSortModelChange = (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        if (!sort) return

        const order = sort.toUpperCase() as 'ASC' | 'DESC'
        const nextFilters = { ...currentFilters, page: 1 }

        if (isTotalValueSortField(field)) {
          setPage(1)
          void sortOrdersByTotal(order, nextFilters)
          return
        }

        setClientSortedPool(null)
        const orderBy = {
          field: mapGridOrderFieldToApi(field),
          order,
        };
        setPage(1)
        listOrders({ ...nextFilters, orderBy, page: 1 });
      } else {
        setClientSortedPool(null)
        const { orderBy, ...filtersWithoutOrder } = currentFilters;
        setPage(1)
        listOrders({ ...filtersWithoutOrder, page: 1 });
      }
    };

    const handleSortModelChangeCancelling = (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        if (!sort) return

        const orderBy = {
          field: mapGridOrderFieldToApi(field),
          order: sort.toUpperCase() as 'ASC' | 'DESC',
        };
        setPageCancelling(1)
        listOrdersCancelling({ ...currentFiltersCancelling, orderBy, page: 1 });
      } else {
        const { orderBy, ...filtersWithoutOrder } = currentFiltersCancelling;
        setPageCancelling(1)
        listOrdersCancelling({ ...filtersWithoutOrder, page: 1 });
      }
    };

    const pickingTotalCount = useMemo(
      () => Object.keys(kanbanSchema?.tasks || {}).length,
      [kanbanSchema?.tasks],
    );

    useEffect(() => {
      listPickingFlow()
    }, [])

    useEffect(() => {
      listOrders({ page: 1})
      listOrdersCancelling({ page: 1 })
  }, [])


    return {
        listLoading,
        cancellingLoading,
        pickingLoading,
        actionLoading,
        loadError,
        loadErrorCancelling,
        listOrders,
        list,
        data,
        handleShowDetails,
        showDetails,
        setShowDetails,
        setShowModalChoices,
        showModalChoices,
        totalRegisters,
        setShowFiltersModal,
        showFiltersModal,
        currentFilters,
        setCurrentFilters,
        handleFilterSelect,
        handleReprocessPayment,
        orderToReprocess,
        setOrderToReprocess,
        handleChange,
        a11yProps,
        value,
        kanbanSchema,
        handleUpdateOrderStatus,
        handlePrintPickingList,
        handleCardClick,
        orderDetailsData,
        orderToCancel,
        setOrderToCancel,
        handleCancelOrder,
        hasCancelPermission,
        listCancelling,
        listOrdersCancelling,
        totalRegistersCancelling,
        handleImportOrder,
        showImportOrderModal,
        setShowImportOrderModal,
        currentFiltersCancelling,
        handlePageChangeOrders,
        handlePageChangeCancelling,
        handleSearch,
        handlePickingSearch,
        refreshPickingFlow,
        clearPickingSearch,
        currentFiltersPickingFlow,
        pickingTotalCount,
        handleSortModelChange,
        handleSortModelChangeCancelling,
        ordersPage: page,
        cancellingPage: pageCancelling,
        ordersSortModel,
        cancellingSortModel,
    }
}
